import { formatChannelOrderNumber, nextChannelSequence } from './pos/channelCounter';
import { OrderChannel } from '../components/constants/OrderChannel';
import { generateDateCode } from './posOrderId';

/**
 * Generate offline-first order number
 * 
 * CORE LOGIC:
 * 1. For CS, TW, DL: Reuses the session's name (assigned at creation).
 *    If no name provided, it generates a NEW one via channelNumberDB (atomic).
 * 2. For TABLE: Appends a sequential number to the table name (e.g., T1-5).
 */

const getOrderNumberPrefix = (orderChannel, tableName = null) => {
  if (orderChannel === OrderChannel.COUNTER || orderChannel === 'COUNTER') return 'CS';
  if (orderChannel === OrderChannel.TAKEAWAY || orderChannel === 'TAKEAWAY') return 'TW';
  if (orderChannel === OrderChannel.DELIVERY || orderChannel === 'DELIVERY' || orderChannel === OrderChannel.DELIVERY_LOCAL || orderChannel === 'DELIVERY_LOCAL') return 'DL';

  if (orderChannel === OrderChannel.TABLE || orderChannel === 'TABLE') {
    if (tableName) {
      const tableNamePart = tableName.split('-')[0];
      if (/^\d/.test(tableNamePart)) return `T${tableNamePart}`;
      return tableNamePart;
    }
    return 'T';
  }
  return 'ORD';
};

export const generateOrderNumber = async (
  storeId,
  orderChannel,
  tableName = null,
  stationStartTime = '04:00'
) => {
  if (!storeId || !orderChannel) {
    throw new Error('storeId and orderChannel are required');
  }

  console.log(`[OrderNumber Debug] generateOrderNumber starting - Channel: ${orderChannel}, Table: ${tableName}, StartTime: ${stationStartTime}`);

  // 1. If it's CS, TW, or DL and we ALREADY HAVE a tableName like "CS001", use it!
  // This satisfies the user's "Assignment happens at session creation" rule.
  const isChannelOrder = [OrderChannel.COUNTER, OrderChannel.TAKEAWAY, OrderChannel.DELIVERY, OrderChannel.DELIVERY_LOCAL, 'COUNTER', 'TAKEAWAY', 'DELIVERY', 'DELIVERY_LOCAL']
    .includes(orderChannel);

  const prefix = getOrderNumberPrefix(orderChannel, tableName);

  const assignedPattern = new RegExp(`^${prefix}(?:[B-D])?(?:Z|F)?\\d+$`, "i");
  if (isChannelOrder && tableName && assignedPattern.test(tableName)) {
    return tableName;
  }

  // 2. If it's a Table order, we need to generate a sub-sequence (e.g., T1-1, T1-2)
  // For now, we'll continue using the existing solid logic for Tables
  const dateCode = await generateDateCode(new Date(), stationStartTime, storeId);

  if (orderChannel === OrderChannel.TABLE || orderChannel === 'TABLE') {
    // Note: Table orders still use posOrderIdDB for their sub-counters for now
    // to avoid breaking existing Table logic as requested.
    const posOrderIdDB = (await import('./indexedDB/posOrderIdDB')).default;
    await posOrderIdDB.init();

    const counterKey = `orderNumber_${storeId}_${dateCode}_${prefix}`;
    const currentCounter = await posOrderIdDB.getCounter(counterKey);
    const nextNumber = currentCounter + 1;
    await posOrderIdDB.setCounter(counterKey, nextNumber);

    if (tableName) {
      const tableNamePart = tableName.split('-')[0];
      const tablePrefix = /^\d/.test(tableNamePart) ? `T${tableNamePart}` : tableNamePart;
      return `${tablePrefix}-${nextNumber}`;
    }
    return `T${String(nextNumber).padStart(4, '0')}`;
  }

  const seq = await nextChannelSequence(storeId, orderChannel, stationStartTime);
  return formatChannelOrderNumber(orderChannel, seq, false, storeId);
};

/**
 * Helper function to check if current user is in training mode
 * @returns {Promise<{isTrainingMode: boolean, userId: string | null}>}
 */
const checkTrainingMode = async () => {
  try {
    // SIMPLE APPROACH: Scan ALL trainingMode_* keys in localStorage
    // Find the first one that is active (not expired)
    // Extract userId from the key name (trainingMode_${userId})
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('trainingMode_') && !key.slice('trainingMode_'.length).includes('_')) {
        try {
          const trainingData = localStorage.getItem(key);
          if (trainingData) {
            const trainingState = JSON.parse(trainingData);
            
            // Check if this training state is active
            if (trainingState.isTrainingMode && trainingState.startTime) {
              const elapsed = Math.floor((Date.now() - trainingState.startTime) / 1000);
              if (elapsed < 5400) { // 90 minutes
                // Extract userId from key (trainingMode_${userId})
                const userId = key.replace('trainingMode_', '');
                console.log(`[Training Mode] orderNumberGenerator.checkTrainingMode: ✅ ACTIVE - userId: ${userId}`);
                return { isTrainingMode: true, userId };
              }
            }
          }
        } catch (e) {
          console.error(`[Training Mode] orderNumberGenerator.checkTrainingMode: Error parsing key ${key}:`, e);
        }
      }
    }
    
    return { isTrainingMode: false, userId: null };
  } catch (error) {
    console.error('[Training Mode] orderNumberGenerator.checkTrainingMode: Fatal error:', error);
    return { isTrainingMode: false, userId: null };
  }
};

const ROUND_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/** Tables 1–999 are T plus 3 digits. A 4-digit name keeps those digits. A letter name is sent as typed. */
export function tableTransactionToken(tableName) {
  const name = String(tableName || "").trim();
  if (/^\d+$/.test(name)) {
    if (name.length <= 3) return `T${name.padStart(3, "0")}`;
    return `T${name.slice(0, 4)}`;
  }
  return name.slice(0, 5);
}

/** Party round and later document suffixes: 01–99, then 0A–0Z, then AA–ZZ. */
export function formatPartyRound(n) {
  const value = Number(n) || 0;
  if (value <= 99) return String(value).padStart(2, "0");
  let index = value - 100;
  if (index < 26) return `0${ROUND_LETTERS[index]}`;
  index -= 26;
  const first = Math.floor(index / 26);
  const second = index % 26;
  if (first >= 26) return "ZZ";
  return `${ROUND_LETTERS[first]}${ROUND_LETTERS[second]}`;
}

/** A table channel number is the party root, or that root plus a 2-character document suffix. */
export function isTableChannelNumber(value) {
  const text = String(value || "").trim();
  return /^(?:T\d{3,4}|[A-Za-z][A-Za-z0-9]{0,4})[0-9A-Z]{2}(?:-[0-9A-Z]{2})?$/.test(text);
}

/** The latest table document number saved on an order, such as T02801-02. */
export function latestTableTransaction(order) {
  const history = Array.isArray(order?.printHistory) ? order.printHistory : [];
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const number = history[index]?.transactionNumber;
    if (isTableChannelNumber(number)) return String(number).trim();
  }
  return "";
}

/** Party root for a table order: T02801 from T02801-02, or the session id when it is already a table number. */
export function tablePartyRoot(order) {
  const latest = latestTableTransaction(order);
  if (latest) return latest.replace(/-[0-9A-Z]{2}$/, "");
  if (isTableChannelNumber(order?.sessionId)) return String(order.sessionId).trim();
  if (isTableChannelNumber(order?.orderNumber)) return String(order.orderNumber).trim().replace(/-[0-9A-Z]{2}$/, "");
  return "";
}

/** First document is the party root. Later documents add a 2-character suffix. */
export function tableDocumentNumber(root, suffix) {
  const value = Number(suffix) || 0;
  if (value <= 0) return String(root || "");
  return `${root}-${formatPartyRound(value)}`;
}

/**
 * One party number per table per day.
 * VP2, first party: VP201. Table 20, first party: T02001.
 * The first document uses this number with no suffix.
 */
export const generateTableSessionId = async (
  storeId,
  tableName,
  stationStartTime = '04:00'
) => {
  if (!storeId || !tableName) {
    throw new Error('storeId and tableName are required');
  }

  const { isTrainingMode, userId } = await checkTrainingMode();
  const dateCode = await generateDateCode(new Date(), stationStartTime, storeId);
  const token = tableTransactionToken(tableName);
  const key = isTrainingMode && userId
    ? `tableRound_${storeId}_${dateCode}_${token}_TRAINING_${userId}`
    : `tableRound_${storeId}_${dateCode}_${token}`;
  const posOrderIdDB = (await import('./indexedDB/posOrderIdDB')).default;
  await posOrderIdDB.init();

  const currentCounter = await posOrderIdDB.getCounter(key);
  const nextNumber = (currentCounter || 0) + 1;
  await posOrderIdDB.setCounter(key, nextNumber);

  let round = formatPartyRound(nextNumber);
  if (isTrainingMode) round = round.replace(/^0/, "F");
  const sessionId = `${token}${round}`;

  console.log(`[TableSession Debug] Generated session ID: ${sessionId} for table: ${tableName}`);
  return sessionId;
};

export default {
  generateOrderNumber,
  getOrderNumberPrefix,
  generateTableSessionId,
  tableTransactionToken,
  formatPartyRound,
  tableDocumentNumber,
  isTableChannelNumber,
};
